<template>
  <div class="m-grouptable-wrapper">
    <DataTable
      :value="groupedRows"
      :loading="loading"
      v-model:selection="selectedRows"
      :rowGroupMode="'subheader'"
      :groupRowsBy="groupBy"
      :sortMode="'single'"
      :sortField="groupBy"
      :sortOrder="1"
      :selection-mode="selectionMode"
      :class="tableClasses"
      @row-select="handleRowSelect"
      @row-unselect="handleRowUnselect"
      @row-contextmenu="handleContextMenu"
    >
      <!-- Group header template -->
      <template #groupheader="slotProps">
        <div class="group-header">
          <i class="pi pi-tag"></i>
          <span class="group-name">{{ getGroupName(slotProps.data) }}</span>
          <span class="group-count">({{ getGroupCount(slotProps.data) }})</span>
        </div>
      </template>

      <!-- Columns -->
      <Column
        v-for="(header, index) in headers"
        :key="index"
        :field="header.value"
        :header="header.name"
        :sortable="sortable"
      >
        <template #body="slotProps">
          <div class="table-cell">
            {{ getCellValue(slotProps.data, header.value) }}
          </div>
        </template>
      </Column>

      <!-- Empty state -->
      <template #empty>
        <div class="empty-state">
          <i class="pi pi-table"></i>
          <p>Нет данных для отображения</p>
        </div>
      </template>
    </DataTable>

    <!-- Pagination -->
    <div v-if="props.showControlPanel && hasPagination" class="m-grouptable-pagination">
      <MPagination
        :page="currentPage"
        :pages="totalPages"
        :items-per-page="itemsPerPage"
        @update:page="handlePageChange"
        @update:itemsPerPage="handleItemsPerPageChange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import MPagination from '@/components/ui/MPagination.vue'
import { computed, ref } from 'vue'

interface Props {
  items?: any[]
  headers?: any[]
  groupBy?: string
  sortable?: boolean
  selectionMode?: 'single' | 'multiple' | null
  meta?: {
    page?: number
    pages?: number
    itemsPerPage?: number
  }
  loading?: boolean
  showTableHeader?: boolean
  showControlPanel?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  headers: () => [],
  groupBy: '_group',
  sortable: true,
  selectionMode: 'multiple',
  meta: () => ({ page: 1, pages: 1, itemsPerPage: 50 }),
  loading: false,
  showTableHeader: true,
  showControlPanel: true,
})

const emit = defineEmits<{
  'row-select': [row: any]
  'row-unselect': [row: any]
  'context-menu': [event: { row: any; event: MouseEvent }]
  'update:meta': [meta: any]
}>()

const selectedRows = ref<any[]>([])

const currentPage = computed(() => props.meta?.page || 1)
const totalPages = computed(() => props.meta?.pages || 1)
const itemsPerPage = computed(() => props.meta?.itemsPerPage || 50)
const hasPagination = computed(() => totalPages.value > 1)

// Group rows by the groupBy field
const groupedRows = computed(() => {
  if (!props.items || props.items.length === 0) return []
  
  // Add group field to each row if it doesn't exist
  return props.items.map(row => ({
    ...row,
    [props.groupBy]: row[props.groupBy] || 'Без группы'
  }))
})

const tableClasses = computed(() => [
  'm-grouptable',
  {
    'm-grouptable--loading': props.loading,
    'm-grouptable--without-header': props.showTableHeader === false,
  }
])

// Decode HTML entities in text
const decodeHtmlEntities = (text: string): string => {
  if (typeof text !== 'string') return text
  
  // Create a temporary DOM element to decode HTML entities
  const textarea = document.createElement('textarea')
  textarea.innerHTML = text
  return textarea.value
}

const getCellValue = (row: any, field: string): any => {
  const value = row[field]
  let result: any
  
  if (value && typeof value === 'object' && 'value' in value) {
    result = value.value
  } else {
    result = value
  }
  
  // Decode HTML entities if result is a string
  if (typeof result === 'string') {
    return decodeHtmlEntities(result)
  }
  
  return result
}

const getGroupName = (row: any): string => {
  return row[props.groupBy] || 'Без группы'
}

const getGroupCount = (row: any): number => {
  const groupValue = row[props.groupBy]
  return props.items.filter(item => item[props.groupBy] === groupValue).length
}

const handleRowSelect = (event: any) => {
  emit('row-select', event.data)
}

const handleRowUnselect = (event: any) => {
  emit('row-unselect', event.data)
}

const handleContextMenu = (event: any) => {
  emit('context-menu', { row: event.data, event: event.originalEvent })
}

const handlePageChange = (page: number) => {
  emit('update:meta', { ...props.meta, page })
}

const handleItemsPerPageChange = (itemsPerPage: number) => {
  emit('update:meta', { ...props.meta, itemsPerPage, page: 1 })
}
</script>

<style scoped lang="scss">
.m-grouptable-wrapper {
  width: 100%;
}

.m-grouptable {
  width: 100%;

  :deep(.p-datatable-table) {
    width: 100%;
  }

  :deep(.p-datatable-thead) {
    background: #f9fafb;
  }

  &.m-grouptable--without-header {
    :deep(.p-datatable-thead) {
      display: none;
    }
  }

  :deep(.p-datatable-tbody) {
    .p-rowgroup-header {
      background: #eff6ff;
      font-weight: 600;
      color: #1e40af;
    }
  }
}

.group-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  
  .group-name {
    font-weight: 600;
  }
  
  .group-count {
    color: #6b7280;
    font-size: 0.875rem;
  }
}

.table-cell {
  padding: 0.5rem;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #9ca3af;

  i {
    font-size: 3rem;
    margin-bottom: 1rem;
  }

  p {
    margin: 0;
    font-size: 1rem;
  }
}

.m-grouptable-pagination {
  margin-top: 1rem;
}
</style>

